/**
 * Type-safe Event Emitter
 *
 * Zero-allocation event system for game communication
 */

type EventCallback<T = unknown> = (data: T) => void;

interface EventEntry<T = unknown> {
  callback: EventCallback<T>;
  context: unknown;
  once: boolean;
}

export class EventEmitter<TEvents = Record<string, unknown>> {
  private listeners: Map<keyof TEvents, EventEntry[]> = new Map();

  /**
   * Subscribe to an event
   */
  on<K extends keyof TEvents>(
    event: K,
    callback: EventCallback<TEvents[K]>,
    context?: unknown
  ): this {
    let entries = this.listeners.get(event);
    if (!entries) {
      entries = [];
      this.listeners.set(event, entries);
    }

    entries.push({
      callback: callback as EventCallback,
      context,
      once: false,
    });

    return this;
  }

  /**
   * Subscribe to an event once
   */
  once<K extends keyof TEvents>(
    event: K,
    callback: EventCallback<TEvents[K]>,
    context?: unknown
  ): this {
    let entries = this.listeners.get(event);
    if (!entries) {
      entries = [];
      this.listeners.set(event, entries);
    }

    entries.push({
      callback: callback as EventCallback,
      context,
      once: true,
    });

    return this;
  }

  /**
   * Unsubscribe from an event
   */
  off<K extends keyof TEvents>(
    event: K,
    callback?: EventCallback<TEvents[K]>,
    context?: unknown
  ): this {
    const entries = this.listeners.get(event);
    if (!entries) return this;

    if (!callback) {
      // Remove all listeners for this event
      this.listeners.delete(event);
      return this;
    }

    // Find and remove specific listener
    for (let i = entries.length - 1; i >= 0; i--) {
      const entry = entries[i];
      if (entry.callback === callback && (!context || entry.context === context)) {
        entries.splice(i, 1);
      }
    }

    if (entries.length === 0) {
      this.listeners.delete(event);
    }

    return this;
  }

  /**
   * Emit an event
   */
  emit<K extends keyof TEvents>(event: K, data: TEvents[K]): boolean {
    const entries = this.listeners.get(event);
    if (!entries || entries.length === 0) return false;

    // Copy array in case listeners modify it
    const toCall = entries.slice();
    const toRemove: EventEntry[] = [];

    for (const entry of toCall) {
      entry.callback.call(entry.context, data);

      if (entry.once) {
        toRemove.push(entry);
      }
    }

    // Remove one-time listeners
    for (const entry of toRemove) {
      const index = entries.indexOf(entry);
      if (index !== -1) {
        entries.splice(index, 1);
      }
    }

    return true;
  }

  /**
   * Check if event has listeners
   */
  has<K extends keyof TEvents>(event: K): boolean {
    const entries = this.listeners.get(event);
    return !!entries && entries.length > 0;
  }

  /**
   * Get listener count for an event
   */
  listenerCount<K extends keyof TEvents>(event: K): number {
    const entries = this.listeners.get(event);
    return entries?.length ?? 0;
  }

  /**
   * Remove all listeners
   */
  removeAllListeners(): this {
    this.listeners.clear();
    return this;
  }
}

// ============================================================================
// ENGINE EVENTS
// ============================================================================

export interface EngineEvents {
  'engine:start': void;
  'engine:stop': void;
  'engine:pause': void;
  'engine:resume': void;
  'engine:resize': { width: number; height: number };
  'engine:focus': void;
  'engine:blur': void;
}

export interface GameEvents {
  'game:start': { gameType: string; roomId: string };
  'game:end': { winnerId: string; results: unknown };
  'game:state_change': { from: string; to: string };
  'game:turn_start': { playerId: string };
  'game:roll_start': { playerId: string };
  'game:roll_end': { playerId: string; dice: number[] };
  'game:bet_placed': { playerId: string; amount: number };
  'game:payout': { playerId: string; amount: number };
}

export interface InputEvents {
  'input:touch_start': { x: number; y: number; id: number };
  'input:touch_move': { x: number; y: number; id: number; dx: number; dy: number };
  'input:touch_end': { x: number; y: number; id: number };
  'input:pointer_down': { x: number; y: number; button: number };
  'input:pointer_move': { x: number; y: number; dx: number; dy: number };
  'input:pointer_up': { x: number; y: number; button: number };
}

export interface DiceEvents {
  'dice:pickup': { diceIndex: number };
  /** Throw state machine transitioned (READY / PICKING_UP / ... / STOPPED).
   *  Edge-driven consumers (banner suppression) subscribe here instead of
   *  polling controller.state per frame. */
  'dice:throw_state': { state: string };
  /** One shake beat while holding. intensity 0..1 from stroke speed. */
  'dice:shake': { intensity: number };
  'dice:all_collected': void;
  'dice:energy_change': { energy: number; maxEnergy: number };
  'dice:throw': { direction: { x: number; y: number }; power: number };
  'dice:rolling': void;
  'dice:stopped': { values: number[] };
  'dice:reset': void;
  // Network sync events (legacy)
  'dice:drag_sync': {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number; w: number };
    seq: number;
    timestamp: number;
  };
  'dice:throw_sync': {
    diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }>;
  };
  // Server-authoritative request events
  'dice:pickup_start_request': { timestamp: number };
  'dice:collected_request': { timestamp: number };
  'dice:throw_request': {
    diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }>;
    halfExtents?: [number, number, number];
    halfExtentsPerDie?: Array<[number, number, number]>;
    timestamp: number;
  };
  'dice:stopped_request': {
    results: number[];
    finalStates: Array<{
      position: { x: number; y: number; z: number };
      quaternion: { x: number; y: number; z: number; w: number };
    }>;
    timestamp: number;
  };
  // Held dice released WITHOUT a throw — broadcast transforms so spectators
  // replay an identical (zero-velocity) drop instead of seeing frozen dice.
  'dice:drop_request': {
    diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }>;
    halfExtents?: [number, number, number];
    halfExtentsPerDie?: Array<[number, number, number]>;
    timestamp: number;
  };
  // Remote player events (for ghost dice)
  'dice:remote_phase': {
    playerId: string;
    phase: string;
    throwParams?: {
      diceStates: Array<{
        position: { x: number; y: number; z: number };
        rotation: { x: number; y: number; z: number; w: number };
        velocity: { x: number; y: number; z: number };
        angularVelocity: { x: number; y: number; z: number };
      }>;
      timestamp: number;
    };
  };
  'dice:remote_throw': {
    playerId: string;
    throwParams: {
      diceStates: Array<{
        position: { x: number; y: number; z: number };
        rotation: { x: number; y: number; z: number; w: number };
        velocity: { x: number; y: number; z: number };
        angularVelocity: { x: number; y: number; z: number };
      }>;
      timestamp: number;
    };
  };
}

// Combined event types
export type AllEvents = EngineEvents & GameEvents & InputEvents & DiceEvents;
